import { type MenuItem } from '@direct-frontend/components';
import { type BaseRowData } from '@adv-frontend/data-provider';
import { type GridColumnHelper } from '@adv-frontend/grid';
import { CellExpand, CellExpandStub } from '../components/CellExpand';
import { CellHeaderSelect } from '../components/CellHeaderSelect';
import { CellMenu } from '../components/CellMenu';
import { CellSelect, CellSelectStub } from '../components/CellSelect';
import { getCellDataTestId } from './getCellDataTestId';
import { type GridRow, type GridValidatorOptions, type GridValidatorType } from './types';

export type SelectColumnHelperOptions = {
    /**
     * Идентификатор для тестирования
     */
    dataTestId?: string;
    /**
     * Флаг генерации колонки для стаба
     */
    forStub?: boolean;
    /**
     * Необходим ли отступ слева. Если отсутствует чекбокс, то нужен левым отступ
     */
    hasLeftPadding?: boolean;
};

/*
 * Вспомогательная функция для генерации колонки с разворачиванием строки
 * @param columnHelper - GridColumnHelper
 * @param options - доп настройки для генерации колонки
 */
export function expandColumnHelper<RowData extends BaseRowData = GridRow>(
    columnHelper: GridColumnHelper<RowData>,
    options: SelectColumnHelperOptions
) {
    const { forStub = false, hasLeftPadding = false } = options;

    return columnHelper.column({
        id: 'expand',
        size: hasLeftPadding ? 32 : 25,
        renderCell: forStub
            ? () => null
            : (props) => <CellExpand {...props} leftPadding={hasLeftPadding} data-testid={getCellDataTestId(props)} />,
        renderHeader: () => null,
        hasDelimiter: false,
        renderStub: () => <CellExpandStub leftPadding={hasLeftPadding} />,
    });
}

/*
 * Вспомогательная функция для генерации колонки с выбором строки
 * @param columnHelper - GridColumnHelper
 * @param label - Подсказка для селектора
 * @param options - доп настройки для генерации колонки
 */
export function selectColumnHelper<ColumnId extends string, RowData extends BaseRowData = GridRow>({
    columnHelper,
    label,
    options,
    id,
    gridValidatorType,
    gridValidatorOptions,
    disableSelectAll,
    disableNewChecked = false,
}: {
    columnHelper: GridColumnHelper<RowData>;
    label: string;
    options: SelectColumnHelperOptions;
    id: ColumnId;
    gridValidatorType?: GridValidatorType;
    gridValidatorOptions?: GridValidatorOptions;
    disableSelectAll?: boolean;
    disableNewChecked?: boolean;
}) {
    const { forStub = false, dataTestId, hasLeftPadding = true } = options;

    return columnHelper.column({
        id,
        size: hasLeftPadding ? 40 : 32,
        hasDelimiter: false,
        renderStub: () => <CellSelectStub />,
        renderCell: forStub
            ? () => null
            : (props) => (
                  <CellSelect
                      {...props}
                      data-testid={getCellDataTestId(props)}
                      label={label}
                      gridValidatorType={gridValidatorType}
                      gridValidatorOptions={gridValidatorOptions}
                      disableNewChecked={disableNewChecked}
                  />
              ),
        renderHeader: (props) => (
            <CellHeaderSelect
                {...props}
                isDisabled={forStub}
                data-testid={dataTestId ? `${dataTestId}.Column.${props.column.id}` : undefined}
                disableSelectAll={disableSelectAll}
            />
        ),
    });
}

/*
 * Вспомогательная функция для генерации колонки с выбором строки
 * @param columnHelper - GridColumnHelper
 * @param label - Подсказка для селектора
 * @param options - доп настройки для генерации колонки
 */
export function menuColumnHelper<RowData extends BaseRowData = GridRow>(
    columnHelper: GridColumnHelper<RowData>,
    getItems: (data: RowData) => MenuItem[],
    options: SelectColumnHelperOptions,
    id: string = 'menu'
) {
    const { forStub = false } = options;

    return columnHelper.column({
        id,
        size: 32,
        hasDelimiter: false,
        renderStub: () => <CellSelectStub />,
        renderCell: forStub
            ? () => null
            : (props) => <CellMenu {...props} getItems={getItems} data-testid={getCellDataTestId(props)} />,
        renderHeader: () => null,
    });
}
